import {
  Avatar,
  AvatarUser,
  Box,
  FlexBox,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ConversationsChannels } from 'lib-shared-legacy-types';
import { useEffect, useMemo, useState } from 'react';
import { useDebounce, useUnmount } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { appActions } from 'app/app.actions';
import { ConversationChannelIcon } from 'components/conversation/conversationChannelIcon';
import {
  CommandPaletteAction,
  CommandPaletteScope,
  useCommandPalette,
} from 'utils/commandPalette';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import styles from '../../commandPalette.module.scss';
import { getCommandPaletteActionItems } from '../../utils/getCommandPaletteActionSelectionListItems';

/** Represents the conversation scope list of the command palette. */
export const ConversationScopeList = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const {
    isActive,
    scope,
    scopeQuery,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
  } = useCommandPalette();
  const isInScope = scope === CommandPaletteScope.Conversation;

  const [readConversation, readConversationRequest] =
    conversationsApi.useLazyReadConversationQuery();

  const [actions, setActions] = useState<readonly CommandPaletteAction[]>([]);

  const handleSelectionListOnChange = (
    event: SelectionListChangeEvent<CommandPaletteAction>,
  ) => {
    if (event.selectedItem.isNode) {
      return;
    }

    performAction(event.selectedItem.value);
    dispatch(appActions.hideModal({ type: 'app:commandPalette' }));
  };

  // Handles fetching the conversation.
  useDebounce(
    () => {
      if (isInScope && scopeQuery && !isNaN(parseInt(scopeQuery))) {
        readConversation({ csid: parseInt(scopeQuery) });
      }
    },
    300,
    [isInScope, readConversation, scopeQuery],
  );

  // Handles creating the actions.
  useEffect(() => {
    if (readConversationRequest.data?.data) {
      setActions([
        createAction({
          label: 'View conversation',
          icon: 'chat',
          performFn: () => {
            navigate({
              path: '/conversation/:csid',
              params: { path: { csid: scopeQuery } },
            });
          },
        }),
        createAction({
          label: 'View activity log',
          icon: 'activity',
          performFn: () => {
            navigate({
              path: '/conversation/:csid/activity-log',
              params: { path: { csid: scopeQuery } },
            });
          },
        }),
        createAction({
          label: 'View requester',
          icon: 'user',
          performFn: () => {
            const requestedBy =
              readConversationRequest.data?.data.history.main_conversation
                .requested_by;
            if (requestedBy) {
              navigate({
                path: '/contacts/user/:id',
                params: { path: { id: requestedBy } },
              });
            }
          },
        }),
      ]);
    }
  }, [createAction, navigate, readConversationRequest.data, scopeQuery]);

  // Handles registering the actions.
  useEffect(() => {
    const unregister = registerActions(actions);

    return () => unregister();
  }, [actions, registerActions]);

  const asyncStatus = useMemo(
    () => getAsyncStatusFromRtkQueryStates(readConversationRequest),
    [readConversationRequest],
  );

  useEffect(() => setAsyncStatus(asyncStatus), [asyncStatus, setAsyncStatus]);

  useUnmount(() => setAsyncStatus('fulfilled'));

  if (!isInScope) {
    return <></>;
  }

  const conversationData = readConversationRequest.data?.data;
  const mainConversation = conversationData?.history.main_conversation;
  const requester = mainConversation
    ? mainConversation.authors[mainConversation.requested_by]
    : null;
  const subject = mainConversation?.attributes?.subject;
  const channel = mainConversation?.initial_channel as
    | ConversationsChannels
    | undefined;

  // Show guidance when no conversation ID is entered or invalid
  const hasNoQuery = !scopeQuery || scopeQuery.trim().length === 0;
  const hasInvalidQuery =
    scopeQuery && scopeQuery.trim().length > 0 && isNaN(parseInt(scopeQuery));
  const isLoading = asyncStatus === 'pending';

  // Show guidance when:
  // - No query entered, OR
  // - Invalid query (not a number), AND
  // - Not currently loading (to avoid showing guidance while fetching)
  // - No actions available (no conversation found or not yet loaded)
  if ((hasNoQuery || hasInvalidQuery) && !isLoading && actions.length === 0) {
    return (
      <Box padding="300" className={styles.emptyState}>
        <FlexBox flexDirection="column" alignItems="center" gap="100">
          {hasInvalidQuery ? (
            <span>Please enter a valid conversation ID</span>
          ) : (
            <span>Type a conversation ID to find a conversation</span>
          )}
        </FlexBox>
      </Box>
    );
  }

  if (actions.length === 0) {
    return <></>;
  }

  return (
    <>
      {mainConversation && requester && (
        <Box padding="300">
          <FlexBox alignItems="center" gap="300">
            <Avatar size="32" user={requester as AvatarUser} />
            <FlexBox flexDirection="column" gap="100">
              <FlexBox alignItems="center" gap="200">
                {channel && <ConversationChannelIcon channel={channel} />}
                <span className={styles.conversationPreviewTitle}>
                  {subject || `Conversation #${scopeQuery}`}
                </span>
              </FlexBox>
              <span className={styles.conversationPreviewSubtitle}>
                {requester.name || requester.email || 'Unknown contact'}
              </span>
            </FlexBox>
          </FlexBox>
        </Box>
      )}

      <SelectionList isActive={isActive} onChange={handleSelectionListOnChange}>
        {getCommandPaletteActionItems(actions)}
      </SelectionList>
    </>
  );
};
